import { useState, useMemo, useEffect } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Search, Loader2, MessageSquare, Users, Hash, X, BellOff } from 'lucide-react'
import { OnlineIndicator } from './OnlineIndicator'
import { useConversaciones, useObtenerOCrearConversacion, useCrearGrupo, useCrearChannel, useBuscarMensajes } from '@/core/hooks/useChat'
import { useChatContext } from '@/core/providers/ChatProvider'
import { useUsuariosOrg } from '@/hooks/useUsuarios'
import { format, formatDistanceToNow, parseISO } from 'date-fns'
import { es } from 'date-fns/locale'
import { cn } from '@/lib/utils'
import type { ConversacionConPreview } from '@/core/types/chat'
import { useUserNameResolver } from '@/core/hooks/useUserNameResolver'

type NewChatMode = null | 'direct' | 'group' | 'channel'

interface ConversacionesListProps {
  onSelectConversacion: (conv: ConversacionConPreview) => void
}

export function ConversacionesList({ onSelectConversacion }: ConversacionesListProps) {
  const { user } = useAuth0()
  const { isOnline, setMutedConversations } = useChatContext()
  const { data: conversaciones = [], isLoading } = useConversaciones()
  const { data: usuarios = [] } = useUsuariosOrg({ activo: true })
  const crearConversacion = useObtenerOCrearConversacion()
  const crearGrupo = useCrearGrupo()
  const crearChannel = useCrearChannel()
  const resolveNombre = useUserNameResolver()

  const [busqueda, setBusqueda] = useState('')
  const [newMode, setNewMode] = useState<NewChatMode>(null)
  const [groupName, setGroupName] = useState('')
  const [channelName, setChannelName] = useState('')
  const [selectedMembers, setSelectedMembers] = useState<Set<string>>(new Set())

  const myEmail = user?.email ?? ''
  const { data: globalSearchResults } = useBuscarMensajes(busqueda)

  // Sync muted conversation IDs with ChatProvider
  useEffect(() => {
    const mutedIds = new Set(conversaciones.filter((c) => c.muted).map((c) => c.id))
    setMutedConversations(mutedIds)
  }, [conversaciones, setMutedConversations])
  // NOTA: unread count ya NO se sincroniza desde acá.
  // El ChatProvider llama useConversaciones() internamente y deriva unreadCount.

  // Split conversations by type (with dedup for directs as safety net)
  const { channels, directs, groups } = useMemo(() => {
    const filtered = conversaciones.filter((c) =>
      !busqueda || c.otro_usuario.toLowerCase().includes(busqueda.toLowerCase())
    )
    // Dedup directs: keep only the most recent conversation per otro_usuario
    const directsRaw = filtered.filter((c) => c.tipo === 'direct')
    const directsSeen = new Map<string, ConversacionConPreview>()
    for (const d of directsRaw) {
      const existing = directsSeen.get(d.otro_usuario)
      if (!existing || (d.ultimo_mensaje_at ?? '') > (existing.ultimo_mensaje_at ?? '')) {
        directsSeen.set(d.otro_usuario, d)
      }
    }
    return {
      channels: filtered.filter((c) => c.tipo === 'channel'),
      directs: [...directsSeen.values()],
      groups: filtered.filter((c) => c.tipo === 'group'),
    }
  }, [conversaciones, busqueda])

  // Users not already in a direct conversation
  const existingDirectEmails = new Set(directs.map((c) => c.otro_usuario))
  const availableUsers = usuarios.filter(
    (u) => u.email !== myEmail && !existingDirectEmails.has(u.email)
  )
  const allOtherUsers = usuarios.filter((u) => u.email !== myEmail)

  const handleNewDirect = async (email: string) => {
    const convId = await crearConversacion.mutateAsync(email)
    setNewMode(null)
    onSelectConversacion({ id: convId, otro_usuario: email, tipo: 'direct' } as ConversacionConPreview)
  }

  const handleNewGroup = async () => {
    if (!groupName.trim() || selectedMembers.size < 1) return
    // ORR-281 — la fila completa viene de vuelta con `created_by`/`member_count` reales, en vez
    // de armarse acá a mano sin ellos (eso hacía aparecer "0 miembros" hasta volver al listado).
    const conversacion = await crearGrupo.mutateAsync({ nombre: groupName, miembros: Array.from(selectedMembers) })
    setNewMode(null)
    setGroupName('')
    setSelectedMembers(new Set())
    onSelectConversacion(conversacion)
  }

  const handleNewChannel = async () => {
    if (!channelName.trim()) return
    const convId = await crearChannel.mutateAsync(channelName)
    setNewMode(null)
    setChannelName('')
    onSelectConversacion({ id: convId, otro_usuario: channelName, tipo: 'channel', nombre: channelName } as ConversacionConPreview)
  }

  const toggleMember = (email: string) => {
    setSelectedMembers((prev) => {
      const next = new Set(prev)
      if (next.has(email)) next.delete(email); else next.add(email)
      return next
    })
  }

  const renderConvItem = (conv: ConversacionConPreview) => {
    const nombre = conv.tipo === 'direct' ? resolveNombre(conv.otro_usuario) : (conv.nombre ?? conv.otro_usuario)
    const online = conv.tipo === 'direct' ? isOnline(conv.otro_usuario) : undefined
    const icon = conv.tipo === 'channel' ? '#' : conv.tipo === 'group' ? '👥' : nombre[0]?.toUpperCase()
    const iconBg = conv.tipo === 'channel' ? 'bg-blue-500/10 text-blue-600' : conv.tipo === 'group' ? 'bg-violet-500/10 text-violet-600' : 'bg-primary/10 text-primary'

    return (
      <button
        key={conv.id}
        onClick={() => onSelectConversacion(conv)}
        className={cn(
          'flex items-center gap-2.5 w-full px-3 py-2.5 hover:bg-accent/50 transition-colors text-left',
          conv.no_leidos > 0 && 'bg-accent/20'
        )}
      >
        <div className="relative shrink-0">
          <div className={cn('h-9 w-9 rounded-full flex items-center justify-center text-sm font-medium', iconBg)}>
            {icon}
          </div>
          {online !== undefined && <OnlineIndicator online={online} className="absolute -bottom-0.5 -right-0.5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium truncate">{nombre}</span>
            {conv.ultimo_mensaje_at && (
              <span className="text-[10px] text-muted-foreground shrink-0 ml-2">
                {formatDistanceToNow(parseISO(conv.ultimo_mensaje_at), { addSuffix: false, locale: es })}
              </span>
            )}
          </div>
          {conv.ultimo_mensaje && (
            <p className="text-xs text-muted-foreground truncate mt-0.5">
              {conv.ultimo_sender === myEmail ? 'Vos: ' : conv.tipo !== 'direct' ? `${resolveNombre(conv.ultimo_sender ?? '').split(' ')[0]}: ` : ''}
              {conv.ultimo_mensaje}
            </p>
          )}
        </div>
        {conv.muted && (
          <BellOff className="h-3 w-3 text-muted-foreground shrink-0" />
        )}
        {conv.no_leidos > 0 && (
          <span className="shrink-0 h-5 min-w-[20px] rounded-full bg-primary text-primary-foreground text-[10px] font-medium flex items-center justify-center px-1.5">
            {conv.no_leidos > 9 ? '9+' : conv.no_leidos}
          </span>
        )}
      </button>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-3 pt-3 pb-2 border-b shrink-0">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold">Chat</h3>
          {newMode ? (
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setNewMode(null)}>
              <X className="h-4 w-4" />
            </Button>
          ) : (
            <div className="flex gap-1">
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setNewMode('direct')} title="Nuevo chat">
                <MessageSquare className="h-3.5 w-3.5" />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setNewMode('group')} title="Nuevo grupo">
                <Users className="h-3.5 w-3.5" />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setNewMode('channel')} title="Nuevo canal">
                <Hash className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
        </div>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Buscar..." className="pl-8 h-8 text-sm" />
        </div>
      </div>

      {/* New chat forms */}
      {newMode === 'direct' && (
        <div className="border-b p-2 bg-muted/30">
          <p className="text-xs text-muted-foreground mb-2 px-1">Nueva conversación:</p>
          <div className="space-y-0.5 max-h-32 overflow-y-auto">
            {availableUsers.map((u) => (
              <button key={u.id} onClick={() => handleNewDirect(u.email)} disabled={crearConversacion.isPending}
                className="flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-sm hover:bg-accent text-left">
                <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary">
                  {(u.nombre || u.email)[0]?.toUpperCase()}
                </div>
                <span className="truncate">{u.nombre || u.email.split('@')[0]}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {newMode === 'group' && (
        <div className="border-b p-2 bg-muted/30 space-y-2">
          <Input value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="Nombre del grupo..." className="h-8 text-sm" />
          <p className="text-xs text-muted-foreground px-1">Seleccionar miembros:</p>
          <div className="space-y-0.5 max-h-28 overflow-y-auto">
            {allOtherUsers.map((u) => (
              <button key={u.id} onClick={() => toggleMember(u.email)}
                className={cn('flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-sm text-left', selectedMembers.has(u.email) ? 'bg-primary/10' : 'hover:bg-accent')}>
                <Checkbox checked={selectedMembers.has(u.email)} className="h-4 w-4" />
                <span className="truncate">{u.nombre || u.email.split('@')[0]}</span>
              </button>
            ))}
          </div>
          <Button size="sm" onClick={handleNewGroup} disabled={!groupName.trim() || selectedMembers.size < 1 || crearGrupo.isPending} className="w-full">
            Crear grupo ({selectedMembers.size} miembros)
          </Button>
        </div>
      )}

      {newMode === 'channel' && (
        <div className="border-b p-2 bg-muted/30 space-y-2">
          <Input value={channelName} onChange={(e) => setChannelName(e.target.value)} placeholder="Nombre del canal..." className="h-8 text-sm" />
          <p className="text-xs text-muted-foreground px-1">Todos los miembros de la organización serán agregados automáticamente.</p>
          <Button size="sm" onClick={handleNewChannel} disabled={!channelName.trim() || crearChannel.isPending} className="w-full">
            Crear canal
          </Button>
        </div>
      )}

      {/* Conversations list by section */}
      <ScrollArea className="flex-1">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : conversaciones.length === 0 && !newMode ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <MessageSquare className="h-8 w-8 text-muted-foreground/50 mb-2" />
            <p className="text-sm text-muted-foreground">Sin conversaciones</p>
          </div>
        ) : (
          <div className="py-1">
            {/* Channels */}
            {channels.length > 0 && (
              <>
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-3 py-1.5">Canales</p>
                {channels.map(renderConvItem)}
              </>
            )}

            {/* Direct */}
            {directs.length > 0 && (
              <>
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-3 py-1.5 mt-1">Directos</p>
                {directs.map(renderConvItem)}
              </>
            )}

            {/* Groups */}
            {groups.length > 0 && (
              <>
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-3 py-1.5 mt-1">Grupos</p>
                {groups.map(renderConvItem)}
              </>
            )}

            {/* Global search results */}
            {busqueda.length >= 2 && globalSearchResults && globalSearchResults.length > 0 && (
              <>
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider px-3 py-1.5 mt-1">Mensajes</p>
                {globalSearchResults.slice(0, 5).map((msg) => (
                  <button
                    key={msg.id}
                    onClick={() => {
                      const conv = conversaciones.find((c) => c.id === msg.conversacion_id)
                      if (conv) onSelectConversacion(conv)
                    }}
                    className="flex flex-col w-full px-3 py-2 hover:bg-accent/50 transition-colors text-left"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-muted-foreground">
                        {resolveNombre(msg.sender_email)}
                      </span>
                      <span className="text-[10px] text-muted-foreground">
                        {format(parseISO(msg.created_at), 'dd/MM HH:mm', { locale: es })}
                      </span>
                    </div>
                    <p className="text-sm truncate mt-0.5">{msg.content}</p>
                  </button>
                ))}
              </>
            )}
          </div>
        )}
      </ScrollArea>
    </div>
  )
}
